import React, { useEffect, useMemo } from 'react';
import { Plus } from 'lucide-react';
import { SOURCE_META } from '../dataModelsConstants';
import { logDataModels } from '../dataModelsLogger';
import type { DataModel, DataSource, McpTool, ModelTable, Space } from '../dataModelsTypes';
import { formatRelative, sourceById, sourceTypeLabel } from '../dataModelsUtils';
import DataModelsAuditPanel from './DataModelsAuditPanel';
import { ModelIconTile, SourceIconTile, ViewRowButton, ghostBtn, surfaces } from './dataModelsUi';

export default function SpaceOverview({
  isLight,
  space,
  models,
  tables,
  sources,
  tools,
  onCreateModel,
  onSelectModel,
  onOpenModels,
  onOpenSources,
  onOpenAssets,
  onOpenActivity,
}: {
  isLight: boolean;
  space: Space;
  models: DataModel[];
  tables: ModelTable[];
  sources: DataSource[];
  tools: McpTool[];
  onCreateModel: () => void;
  onSelectModel?: (modelId: string) => void;
  onOpenModels: () => void;
  onOpenSources: () => void;
  onOpenAssets: () => void;
  onOpenActivity: () => void;
}) {
  const ui = surfaces(isLight);
  const muted = ui.muted;
  const spaceSources = useMemo(() => sourcesForSpace(tables, sources, space.sourceIds), [tables, sources, space.sourceIds]);

  useEffect(() => {
    logDataModels('app', 'space_overview_shown', { spaceId: space.id, models: models.length, tables: tables.length });
  }, [space.id, models.length, tables.length]);

  return (
    <div className="space-y-6">
      <section className={`border-b pb-4 ${ui.border}`}>
        <h3 className={`mb-3 text-sm font-semibold ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>About this WorkSpace</h3>
        <dl className="grid max-w-xl grid-cols-[140px_minmax(0,1fr)] gap-y-2 text-sm">
          <dt className={muted}>Owner</dt>
          <dd>{space.owner}</dd>
          <dt className={muted}>Domain</dt>
          <dd>{space.domain}</dd>
          <dt className={muted}>Models</dt>
          <dd>{models.length}</dd>
          <dt className={muted}>Assets</dt>
          <dd>{tables.length}</dd>
          <dt className={muted}>MCP tools</dt>
          <dd>{tools.length}</dd>
        </dl>
        <button type="button" onClick={onOpenAssets} className={`mt-3 text-[13px] ${isLight ? 'text-[#1B6CA8]' : 'text-sky-300'}`}>
          Browse assets
        </button>
      </section>

      <article className={`overflow-hidden rounded-md border ${ui.card}`}>
        <PanelHead
          isLight={isLight}
          title={`Data models (${models.length})`}
          onViewAll={onOpenModels}
          action={
            <button type="button" className={ghostBtn(isLight)} onClick={onCreateModel}>
              <Plus size={14} />
              Create
            </button>
          }
        />
        <CatalogTable
          isLight={isLight}
          headers={['Name', 'Description', 'Assets', 'Updated', '']}
          rows={models.slice(0, 6).map((model) => {
            const modelTables = tables.filter((table) => table.modelId === model.id);
            return {
              key: model.id,
              cells: [
                <span key="name" className="inline-flex items-center gap-2">
                  <ModelIconTile isLight={isLight} box="h-6 w-6" />
                  <span className={`font-medium ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>{model.name}</span>
                </span>,
                <span key="desc" className={`max-w-[280px] truncate ${muted}`}>{model.description}</span>,
                <span key="assets">{modelTables.length}</span>,
                <span key="updated" className={muted}>{formatRelative(model.updatedAt)}</span>,
                onSelectModel ? (
                  <ViewRowButton key="view" isLight={isLight} label={`View ${model.name}`} onClick={() => onSelectModel(model.id)} />
                ) : null,
              ],
            };
          })}
        />
      </article>

      <article className={`overflow-hidden rounded-md border ${ui.card}`}>
        <PanelHead isLight={isLight} title={`Connections (${spaceSources.length})`} onViewAll={onOpenSources} />
        <CatalogTable
          isLight={isLight}
          headers={['Name', 'Type', 'Status']}
          rows={spaceSources.slice(0, 6).map((source) => ({
            key: source.id,
            cells: [
              <span key="name" className="inline-flex items-center gap-2">
                <SourceIconTile kind={source.kind} isLight={isLight} box="h-6 w-6" />
                <span className={`font-medium ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>
                  {SOURCE_META[source.kind].label}
                </span>
              </span>,
              <span key="type" className={muted}>{sourceTypeLabel(source.kind, source.environment)}</span>,
              <span key="status" className={muted}>Connected</span>,
            ],
          }))}
        />
        {!spaceSources.length ? <p className={`px-4 py-6 text-sm ${muted}`}>No connections in this WorkSpace yet.</p> : null}
      </article>

      <DataModelsAuditPanel
        isLight={isLight}
        workspaceId={space.id}
        streams={['workspace', 'catalog', 'asset', 'policy']}
        compact
        limit={4}
        onViewAll={onOpenActivity}
      />
    </div>
  );
}

export function sourcesForSpace(tables: ModelTable[], sources: DataSource[], sourceIds: string[] = []): DataSource[] {
  const ids = new Set([...tables.map((table) => table.sourceId), ...sourceIds]);
  return sources.filter((source) => ids.has(source.id));
}

export function assetKind(table: ModelTable, sources: DataSource[]): 'Tables' | 'Collections' | 'Views' | 'Others' {
  if (table.assetKind === 'File') return 'Others';
  const source = sourceById(sources, table.sourceId);
  if (source?.kind === 'mongodb') return 'Collections';
  if (/view|snapshot/i.test(table.name)) return 'Views';
  if (/bridge|dims|facts/i.test(table.name)) return 'Others';
  return 'Tables';
}

function PanelHead({
  isLight,
  title,
  onViewAll,
  action,
  compact,
}: {
  isLight: boolean;
  title: string;
  onViewAll: () => void;
  action?: React.ReactNode;
  compact?: boolean;
}) {
  const divider = compact ? 'mb-3' : isLight ? 'mb-0 border-b border-indigo-50 px-4 py-3' : 'mb-0 border-b border-indigo-400/10 px-4 py-3';
  return (
    <div className={`flex items-center justify-between gap-2 ${divider}`}>
      <h4 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>{title}</h4>
      <div className="flex items-center gap-2">
        <button type="button" onClick={onViewAll} className={`text-sm ${isLight ? 'text-[#1B6CA8] hover:underline' : 'text-sky-300'}`}>
          View all
        </button>
        {action}
      </div>
    </div>
  );
}

function CatalogTable({
  isLight,
  headers,
  rows,
}: {
  isLight: boolean;
  headers: string[];
  rows: Array<{ key: string; cells: React.ReactNode[] }>;
}) {
  const ui = surfaces(isLight);
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-[13px]">
        <thead className={ui.tableHead}>
          <tr className="text-left text-[11px] font-semibold uppercase tracking-wider">
            {headers.map((header) => (
              <th key={header || 'actions'} className="px-3 py-2.5">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.key} className={`border-t ${ui.border} ${index % 2 === 0 ? ui.row : ui.rowAlt}`}>
              {row.cells.map((cell, cellIndex) => (
                <td key={cellIndex} className="whitespace-nowrap px-3 py-2.5">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
